import { useEffect } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { ApiError, UNAUTHORIZED_EVENT } from '../../api/client.ts';
import { useAuthStatus, useBrand, useEnv } from '../../api/queries.ts';
import { applyBrandToDocument } from '../../lib/brand.ts';
import { useUsageTabs } from '../../lib/usage.ts';
import { ErrorNote, Loading, errorMessage } from '../ui/feedback.tsx';
import { SideNav, TabBar } from './NavBar.tsx';
import { isCanvasRoute } from './navItems.ts';
import { ThemeSwitch } from './ThemeSwitch.tsx';
import { AccountCard, BrandMark, TopBar } from './TopBar.tsx';

/**
 * Everything behind login. Waits for the session and the brand, then draws the
 * navigation and the page. A 401 from any call sends the user to login from
 * wherever it happened.
 *
 * Desktop: the navigation sits on the paper backdrop and the page is one white
 * sheet beside it. Phone: a header above, a tab bar below, the sheet between.
 */
export function AppShell() {
  const auth = useAuthStatus();
  const brand = useBrand();
  const env = useEnv();
  const navigate = useNavigate();
  const location = useLocation();
  useUsageTabs(Boolean(auth.data));

  useEffect(() => {
    const onUnauthorized = () => navigate('/login', { replace: true });
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, [navigate]);

  useEffect(() => {
    if (brand.data) applyBrandToDocument(brand.data);
  }, [brand.data]);

  if (auth.isError && auth.error instanceof ApiError && auth.error.status === 401) {
    return <Navigate to="/login" replace />;
  }
  if (auth.isPending || brand.isPending) {
    return <Loading />;
  }
  if (auth.isError || brand.isError) {
    return (
      <div className="mx-auto max-w-lg p-8">
        <ErrorNote title="The portal could not start">{errorMessage(auth.error ?? brand.error)}</ErrorNote>
      </div>
    );
  }

  const envName = env.data?.env ?? 'production';
  // A canvas page (the signature editor) gets the sheet's whole width.
  const canvas = isCanvasRoute(location.pathname);

  return (
    <div className="flex h-full flex-col md:flex-row">
      {/* The same sidebar on every page, the editor's included, so the bar never changes width. */}
      <aside className="hidden w-[17rem] shrink-0 flex-col px-4 pt-6 pb-4 md:flex">
        <div className="px-3 pb-8">
          <BrandMark brand={brand.data} />
        </div>
        <SideNav brand={brand.data} env={envName} />
        <div className="mt-auto flex flex-col gap-3">
          <ThemeSwitch className="bg-[color-mix(in_oklab,var(--ink)_6%,transparent)]" />
          <AccountCard user={auth.data} />
        </div>
      </aside>

      <TopBar user={auth.data} brand={brand.data} />

      <main className="min-w-0 flex-1 bg-surface pb-24 md:my-3 md:mr-3 md:overflow-y-auto md:rounded-[1.75rem] md:pb-0 md:shadow-[0_1px_2px_rgba(15,27,45,0.04),0_0_0_1px_var(--line)]">
        <div
          key={location.pathname}
          className={
            canvas
              ? 'page-in flex min-h-full flex-col px-5 py-5 md:px-6 md:py-5'
              : 'page-in mx-auto flex min-h-full max-w-[60rem] flex-col px-5 py-7 sm:px-8 md:px-12 md:py-12'
          }
        >
          <Outlet />
        </div>
      </main>

      <TabBar brand={brand.data} env={envName} />
    </div>
  );
}
